import type {
  OrganizationTranslatedString,
  OrganizationTranslatedStringInput,
} from '@generated/graphql';
import type { TranslatedString } from '@interfaces/shared';
import { LanguageCodes } from '@onevesthq/ov-enums';
import { decodeBase64, encodeBase64 } from './appUtils';
import { enumValues } from './enumValues';

/** Rich text editors emit these when the user clears all content. */
const EMPTY_RICH_TEXT_HTML = new Set(['', '<p><br></p>', '<p></p>']);

export type TranslatedRichTextHtml =
  | TranslatedString
  | OrganizationTranslatedString
  | OrganizationTranslatedStringInput
  | null
  | undefined;

export const normalizeRichTextHtml = (
  html: string | null | undefined,
): string => {
  const trimmed = html?.trim() ?? '';
  return EMPTY_RICH_TEXT_HTML.has(trimmed) ? '' : trimmed;
};

export const decodeRichTextHtml = (
  value: string | null | undefined,
): string => {
  if (!value?.trim()) {
    return '';
  }

  try {
    return decodeBase64(value);
  } catch {
    // Values stored before base64 encoding was introduced are plain HTML.
    return value;
  }
};

export const encodeRichTextHtml = (html: string | null | undefined): string => {
  const normalized = normalizeRichTextHtml(html);
  return normalized ? encodeBase64(normalized) : '';
};

export const decodeTranslatedRichTextHtml = (
  value: TranslatedRichTextHtml,
): TranslatedString =>
  Object.fromEntries(
    enumValues(LanguageCodes).map((lang) => [
      lang,
      decodeRichTextHtml(value?.[lang]),
    ]),
  ) as TranslatedString;

export const encodeTranslatedRichTextHtml = (
  value: TranslatedRichTextHtml,
): TranslatedString =>
  Object.fromEntries(
    enumValues(LanguageCodes).map((lang) => [
      lang,
      encodeRichTextHtml(value?.[lang]),
    ]),
  ) as TranslatedString;
